import React, { useEffect, useState, useCallback } from 'react';
import { Button, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Box, Paper } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { getCreditLogs } from '@services/creditLogService';
import type { CreditLog } from '@app-types/models/finance';
import './CreditLogPage.scss';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
dayjs.extend(isBetween);

const CreditLogPage: React.FC = () => {
  const { t } = useTranslation();
  const [creditLogs, setCreditLogs] = useState<CreditLog[]>([]);
  const [filter, setFilter] = useState<string>('all');
  const [stylistFilter, setStylistFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(30, 'day'));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());

  const fetchCreditLogs = useCallback(async () => {
    try {
      const response = await getCreditLogs();
      setCreditLogs(Array.isArray(response.creditLogs) ? response.creditLogs : []);
    } catch (error) {
      console.error('Error fetching credit logs:', error);
    }
  }, []);

  useEffect(() => {
    fetchCreditLogs();
  }, [fetchCreditLogs]);

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
  };

  const handleStylistFilterChange = (newStylistFilter: string) => {
    setStylistFilter(newStylistFilter);
  };

  const filteredLogs = creditLogs.filter((log) => {
    const filterCondition = filter === 'all' ? true : log?.type === filter;
    const stylistCondition = stylistFilter === 'all' ? true : log?.staffId?._id === stylistFilter;
    const dateCondition = dayjs(log?.createdAt).isBetween(startDate, endDate, null, '[]');
    return filterCondition && stylistCondition && dateCondition;
  });

  const uniqueStylistIds = [...new Set(creditLogs.map(log => log?.staffId?._id).filter((id): id is string => id !== undefined))];
  console.log('uniqueStylistIds---', uniqueStylistIds);

  return (
    <Box className="credit-log-page">
      <h2>{t('creditLogPage.title')}</h2>

      <Box className="filter-buttons" display="flex" gap={1} sx={{ width: {xs: 'calc(100vw - 32px)', sm: 'auto'}}}>
        <Button
          variant={filter === 'all' ? 'contained' : 'outlined'}
          onClick={() => handleFilterChange('all')}
        >
          {t('creditLogPage.all')}
        </Button>
        <Button
          variant={filter === 'add' ? 'contained' : 'outlined'}
          onClick={() => handleFilterChange('add')}
        >
          {t('creditLogPage.additions')}
        </Button>
        <Button
          variant={filter === 'deduct' ? 'contained' : 'outlined'}
          onClick={() => handleFilterChange('deduct')}
        >
          {t('creditLogPage.deductions')}
        </Button>
      </Box>

      <Box className="filter-buttons" display="flex" gap={1}>
        <Button
          variant={stylistFilter === 'all' ? 'contained' : 'outlined'}
          onClick={() => handleStylistFilterChange('all')}
        >
          {t('creditLogPage.allStylists')}
        </Button>
        {uniqueStylistIds.map((stylistId) => (
          <Button
            key={stylistId}
            variant={stylistFilter === stylistId ? 'contained' : 'outlined'}
            onClick={() => handleStylistFilterChange(stylistId)}
          >
            {creditLogs.find(log => log?.staffId?._id === stylistId)?.staffId?.displayName || creditLogs.find(log => log?.staffId?._id === stylistId)?.staffId?.email}
          </Button>
        ))}
      </Box>

      <Box className="date-range-container" display="flex" flexDirection="column" alignItems="center" mt={2} mb={2} >
        <Box className="date-picker-container" display="flex" gap={1} mt={1} sx={{ width: {xs: 'calc(100vw - 32px)', sm: 'auto'}}}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue: Dayjs | null) => { if (newValue) setStartDate(newValue); }}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue: Dayjs | null) => { if (newValue) setEndDate(newValue); }}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ overflowX: 'auto', width: 'calc(100vw - 32px)'}}>
        <Table className="credit-log-table">
          <TableHead>
            <TableRow>
              <TableCell>{t('creditLogPage.type')}</TableCell>
              <TableCell>{t('creditLogPage.amount')}</TableCell>
              <TableCell>{t('creditLogPage.balanceBefore')}</TableCell>
              <TableCell>{t('creditLogPage.balanceAfter')}</TableCell>
              <TableCell>{t('creditLogPage.reason')}</TableCell>
              <TableCell>{t('creditLogPage.date')}</TableCell>
              <TableCell>{t('creditLogPage.stylist')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLogs.map((log) => (
              <TableRow key={log._id}>
                <TableCell>{log?.type === 'add' ? t('creditLogPage.addition') : t('creditLogPage.deduction')}</TableCell>
                <TableCell>{log?.amount?.toFixed(2)}</TableCell>
                <TableCell>{log?.balanceBefore?.toFixed(2) || t('creditLogPage.na')}</TableCell>
                <TableCell>{log?.balanceAfter?.toFixed(2)}</TableCell>
                <TableCell>{log?.code || t('creditLogPage.na')}</TableCell>
                <TableCell>{dayjs(log?.createdAt).format('DD/MM/YYYY HH:mm')}</TableCell>
                <TableCell>{log?.staffId?.displayName || log?.staffId?.email}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CreditLogPage;
